import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Calendar, 
  ArrowRight, 
  Grid, 
  Type, 
  LayoutGrid, 
  Search, 
  Gamepad2, 
  Sparkles,
  Thermometer,
  Music,
  Check
} from 'lucide-react';
import SEO from './SEO';
import Header from './Header';

const GameCard = ({ title, description, to, icon, color = "blue", type = "main" }) => {
  const colorClasses = {
    blue: "hover:border-brand-blue/20 hover:bg-brand-blue/5 group-hover:text-brand-blue",
    purple: "hover:border-purple-200 hover:bg-purple-50 group-hover:text-purple-600",
    orange: "hover:border-orange-200 hover:bg-orange-50 group-hover:text-orange-600",
    green: "hover:border-green-200 hover:bg-green-50 group-hover:text-green-600",
    red: "hover:border-red-200 hover:bg-red-50 group-hover:text-red-600",
    yellow: "hover:border-yellow-200 hover:bg-yellow-50 group-hover:text-yellow-600",
  };

  const isSmall = type === "small";

  return (
    <motion.div 
      whileHover={{ y: -5 }}
      className="group"
    >
      <Link 
        to={to}
        className={`flex flex-col h-full bg-white rounded-3xl p-6 md:p-8 border-2 border-gray-50 shadow-sm transition-all duration-300 ${colorClasses[color]}`}
      >
        <div className="flex items-start justify-between mb-6">
          <div className={`p-4 rounded-2xl bg-gray-50 group-hover:scale-110 transition-transform`}>
            {icon}
          </div>
          <ArrowRight className="w-5 h-5 text-gray-300 group-hover:text-current group-hover:translate-x-1 transition-all" />
        </div>

        <h3 className={`font-black text-gray-900 mb-2 transition-colors ${isSmall ? 'text-xl' : 'text-2xl'}`}>
          {title}
        </h3>
        <p className="text-gray-500 text-sm md:text-base leading-relaxed mb-4">
          {description}
        </p>
      </Link>
    </motion.div>
  );
};

const HomePage = () => {
  return (
    <div className="flex flex-col min-h-screen">
      <SEO 
        title="PocketHints - Daily Puzzle Helpers & Reddit Game Guides" 
        description="Your central hub for Pocket Grids, Syllo, and Hot and Cold hints. Find game guides, daily answers, and archives for all your favorite reddit games."
        url={window.location.origin}
      />

      <Header date={new Date().toISOString()} title="Daily Puzzle Directory" gameName="PocketHints Helper Hub" />

      <main className="flex-grow max-w-7xl mx-auto px-4 w-full py-12">
        
        {/* Daily Hints & Answers */}
        <section className="mb-16">
          <div className="flex items-center gap-3 mb-8">
            <div className="h-8 w-2 bg-green-500 rounded-full" />
            <h2 className="text-3xl font-black text-gray-900 uppercase tracking-tight">Today's Hints</h2>
            </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <GameCard 
              title="Today's Pocket Grids"
              description="Get progressive hints and the full answer key for today's grid."
                to="/todays-pocket-grids-clues-answers"
              color="blue"
              type="small"
              icon={<div className="relative">
                <img src={process.env.PUBLIC_URL + '/pocket-grids-logo.png'} className="w-10 h-10 object-contain" alt="PG" />
                <Sparkles className="w-4 h-4 text-brand-blue absolute -top-1 -right-1 bg-white rounded-full p-0.5 shadow-sm" />
              </div>}
            />
            <GameCard 
              title="Today's Syllo"
              description="Syllable breakdowns and solutions for today's 5 Syllo puzzles."
              to="/todays-syllo-clues-answers"
              color="purple"
              type="small"
              icon={<div className="relative">
                <img src={process.env.PUBLIC_URL + '/Syllo-logo.png'} className="w-10 h-10 object-contain" alt="Syllo" />
                <Type className="w-4 h-4 text-purple-600 absolute -top-1 -right-1 bg-white rounded-full p-0.5 shadow-sm" />
              </div>}
            />
            <GameCard 
              title="Today's Hot and Cold"
              description="Temperature checks and the secret word for today's challenge."
              to="/todays-hot-and-cold-clues-answers"
              color="orange"
              type="small"
              icon={<div className="relative">
                <img src={process.env.PUBLIC_URL + '/hotandcold-logo.png'} className="w-10 h-10 object-contain" alt="H&C" />
                <Thermometer className="w-4 h-4 text-orange-600 absolute -top-1 -right-1 bg-white rounded-full p-0.5 shadow-sm" />
              </div>}
                />
            <GameCard 
              title="Today's Byrdle"
              description="Choral music answers."
              to="/todays-byrdle-clues-answers"
              color="green"
              type="small"
              icon={<div className="relative">
                <img src={process.env.PUBLIC_URL + '/byrdle-logo.png'} className="w-10 h-10 object-contain" alt="Byrdle" />
                <Music className="w-4 h-4 text-green-600 absolute -top-1 -right-1 bg-white rounded-full p-0.5 shadow-sm" />
              </div>}
                />
              </div>
          
        </section>

        {/* Main Game Landing Pages */}
        <section className="mb-16">
          <div className="flex items-center gap-3 mb-8">
            <div className="h-8 w-2 bg-brand-blue rounded-full" />
            <h2 className="text-3xl font-black text-gray-900 uppercase tracking-tight">Game Guides</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <GameCard 
              title="Pocket Grids Guide"
              description="Everything you need to know about the Pocket Grids reddit game. Rules, community, and more."
              to="/pocket-grids"
              color="blue"
              icon={<img src={process.env.PUBLIC_URL + '/pocket-grids-logo.png'} className="w-8 h-8 object-contain" alt="Pocket Grids" />}
            />
            <GameCard 
              title="Syllo Guide"
              description="Master the art of merging syllables. Learn the rules and find the Syllo reddit community."
              to="/syllo"
              color="purple"
              icon={<img src={process.env.PUBLIC_URL + '/Syllo-logo.png'} className="w-8 h-8 object-contain" alt="Syllo" />}
            />
            <GameCard 
              title="Hot and Cold Guide"
              description="How semantic similarity works in Hot and Cold. Join the reddit discussion and learn to win."
              to="/hot-and-cold"
              color="orange"
              icon={<img src={process.env.PUBLIC_URL + '/hotandcold-logo.png'} className="w-8 h-8 object-contain" alt="Hot and Cold" />}
                />
            <GameCard 
              title="Byrdle Guide"
              description="Learn about the daily choral music word game. Hints and history."
              to="/byrdle"
              color="green"
              icon={<img src={process.env.PUBLIC_URL + '/byrdle-logo.png'} className="w-8 h-8 object-contain" alt="Byrdle" />}
            />
              </div>
        </section>

        {/* Archives */}
        <section className="mb-16">
          <div className="flex items-center gap-3 mb-8">
            <div className="h-8 w-2 bg-purple-500 rounded-full" />
            <h2 className="text-3xl font-black text-gray-900 uppercase tracking-tight">Puzzle Archives</h2>
            </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
            <GameCard 
              title="Pocket Grids"
              description="Browse past grids"
              to="/archive"
              color="blue"
              type="small"
              icon={<Calendar className="w-5 h-5 text-brand-blue" />}
            />
            <GameCard 
              title="Syllo"
              description="Past syllable puzzles"
                to="/syllo-archive"
              color="purple"
              type="small"
              icon={<Calendar className="w-5 h-5 text-purple-600" />}
            />
            <GameCard 
              title="Hot and Cold"
              description="Previous secret words"
              to="/hot-and-cold-archive"
              color="orange"
              type="small"
              icon={<Calendar className="w-5 h-5 text-orange-600" />}
            />
            <GameCard 
              title="Byrdle"
              description="Past choral puzzles"
              to="/byrdle-archive"
              color="green"
              type="small"
              icon={<Calendar className="w-5 h-5 text-green-600" />}
            />
            <GameCard 
              title="All Archives"
              description="Full index of all games"
              to="/archives"
              color="green"
              type="small"
              icon={<LayoutGrid className="w-5 h-5 text-green-600" />}
                />
              </div>
        </section>

        {/* Directories & Discovery */}
        <section>
          <div className="flex items-center gap-3 mb-8">
            <div className="h-8 w-2 bg-brand-dark rounded-full" />
            <h2 className="text-3xl font-black text-gray-900 uppercase tracking-tight">Discover More</h2>
            </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <GameCard 
              title="Reddit Games Directory"
              description="Explore the best games played directly on Reddit. From word puzzles to collaborative building."
              to="/reddit-games"
              color="red"
              icon={<Gamepad2 className="w-8 h-8 text-red-600" />}
            />
            <GameCard 
              title="DailyGuess Solver"
              description="Instant helper for Wordle and DailyGuess. Enter your tiles to find the answer."
              to="/daily-guess-solver"
              color="yellow"
              icon={<div className="relative">
                <img src={process.env.PUBLIC_URL + '/dailyguess-logo.png'} className="w-8 h-8 object-contain" alt="DailyGuess" />
              </div>}
            />
            <GameCard 
              title="Search Hints"
              description="Search our database for specific clues or answers from past puzzles."
              to="/search"
              color="blue"
              icon={<Search className="w-8 h-8 text-brand-blue" />}
            />
          </div>
        </section>

      </main>

      {/* Feedback Section */}
      <section className="bg-brand-blue/5 py-16 border-t border-gray-200">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <img 
            src={process.env.PUBLIC_URL + '/favicon.png'} 
            alt="PocketHints Logo" 
            className="w-16 h-16 mx-auto mb-6 object-contain drop-shadow-sm"
          />
          <h2 className="text-3xl font-black text-gray-900 mb-4">
            Help Us Improve PocketHints
          </h2>
          <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto leading-relaxed">
            We're always looking for ways to make our hints and guides better for you. Have a suggestion, found a bug, or just want to say hi? We'd love to hear from you!
          </p>
          <a 
            href="https://tally.so/r/BzDVd1" 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-brand-blue text-white text-lg font-bold rounded-xl shadow-lg hover:bg-blue-600 hover:scale-105 transition-all duration-200"
          >
            Share Your Feedback
          </a>
        </div>
      </section>

      {/* Simplified Bottom About Section */}
      <section className="bg-white/50 border-t border-gray-100 py-16 backdrop-blur-sm">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-black text-gray-900 mb-6">Master Your Daily Puzzles</h2>
          <p className="text-gray-500 text-lg leading-relaxed mb-8">
            PocketHints is your companion for the most popular daily games on the web. 
            We provide spoilers-free hints, deep-dive game guides, and a complete history 
            of past puzzles to help you keep your daily streak alive.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/pocket-grids" className="text-brand-blue font-bold hover:underline">Pocket Grids</Link>
            <span className="text-gray-300">•</span>
            <Link to="/syllo" className="text-brand-blue font-bold hover:underline">Syllo</Link>
            <span className="text-gray-300">•</span>
            <Link to="/hot-and-cold" className="text-brand-blue font-bold hover:underline">Hot and Cold</Link>
            <span className="text-gray-300">•</span>
            <Link to="/byrdle" className="text-brand-blue font-bold hover:underline">Byrdle</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
